/** Static metadata for workflows and steps — not available from Mastra API */

export const WORKFLOW_DESCRIPTIONS: Record<string, string> = {
  'meeting-minutes': 'Scans all configured Google Drive folders for new meeting transcript documents and links them to the corresponding meeting record in the database.',
  accountingWorkflow: 'Fetch transactions from QuickBooks, categorize with AI rules, reconcile against invoices, and generate summary report.',
  invoiceSyncWorkflow: 'Pull invoices from ATEK MongoDB, deduplicate against QuickBooks, and create missing invoices in QB.',
  paymentDetectionWorkflow: 'Scan Gmail for RBC deposits, payment notices, and scanned checks, match against open QB invoices, and record payments.',
  arInvestigationWorkflow: 'Pull overdue invoices from QB aging report, investigate across 6 sources, and draft follow-up emails.',
  reconciliationWorkflow: 'OCR bank/credit card statements, deduplicate, detect transfers, match QB records, categorize, find documents, validate tax, and post journal entries.',
  searchWorkflow: 'Cascade search across DMS, Gmail, and Google Drive with auto-upload of found documents to DMS.',
};

/** Per-step human-readable descriptions */
export const STEP_DESCRIPTIONS: Record<string, string> = {
  // meeting-minutes
  'list-transcripts': 'Queries all configured Google Drive meeting folders in parallel. Returns Google Docs modified after the given date, deduplicated and sorted newest first.',
  'process-transcript': 'For each document: finds the closest matching meeting by date (±1 day), checks if already linked, reads the full transcript text, and updates the meeting\'s notes in the database.',

  // accounting
  'fetch-transactions': 'Pulls recent transactions from QuickBooks using SQL queries and date filters.',
  'categorize-transactions': 'Applies AI categorization rules to classify each transaction by type and account.',
  'reconcile': 'Matches categorized transactions against open invoices and payments in QuickBooks.',
  'generate-report': 'Builds a structured summary report of reconciled transactions and discrepancies.',

  // invoice-sync
  'fetch-atek-invoices': 'Retrieves all invoices from the ATEK MongoDB database for the sync period.',
  'map-and-deduplicate': 'Maps ATEK invoice fields to QB format and removes already-synced invoices.',
  'create-in-quickbooks': 'Creates new invoices in QuickBooks for each deduplicated ATEK invoice.',

  // payment-detection
  'scan-payment-emails': 'Scans Gmail for RBC deposit confirmations, e-transfer notices, and scanned cheques.',
  'match-invoices': 'Matches detected payment amounts and references to open QuickBooks invoices.',
  'record-payments': 'Records matched payments in QuickBooks and closes the corresponding invoices.',

  // ar-investigation
  'get-ar-aging': 'Fetches the accounts receivable aging report from QuickBooks.',
  'investigate-invoices': 'Investigates each overdue invoice across email, Drive, DMS, and QB history.',
  'create-followup-drafts': 'Drafts collection follow-up emails for each unresolved overdue invoice.',

  // search
  'cascade-search': 'Searches DMS, Gmail, and Google Drive in sequence, collecting all matching documents.',
  'upload-to-dms': 'Uploads found documents from external sources into the ATEK DMS.',
};

/** Tools used by each step (derived from workflow source code) */
export const STEP_TOOLS: Record<string, string[]> = {
  // accounting
  'fetch-transactions': ['qbQuery', 'searchTransactions'],
  'categorize-transactions': ['matchRule'],
  'reconcile': ['findInvoice', 'findPayment'],
  'generate-report': [],

  // invoice-sync
  'fetch-atek-invoices': ['listInvoices'],
  'map-and-deduplicate': ['qbQuery', 'findCustomer'],
  'create-in-quickbooks': ['createInvoice'],

  // payment-detection
  'scan-payment-emails': ['searchEmails', 'ocrScan', 'detectLabels'],
  'match-invoices': ['qbQuery', 'findInvoice'],
  'record-payments': ['createPayment'],

  // ar-investigation
  'get-ar-aging': ['arAging'],
  'investigate-invoices': ['getInvoice', 'searchEmails', 'ocrScan', 'qbQuery'],
  'create-followup-drafts': [],

  // reconciliation
  'ingest-validate-statement': ['statementOcr'],
  'dedup-check': [],
  'detect-transfers': ['detectTransfer'],
  'match-qb-record': ['qbQuery', 'searchTransactions'],
  'categorize': ['matchRule', 'proposeRule'],
  'find-document': ['searchEmails', 'searchDriveFiles', 'dmsSearch'],
  'tax-validation': [],
  'store-and-attach': ['dmsUpload', 'dmsLink'],
  'post-and-report': ['createJournalEntry'],

  // search
  'cascade-search': ['dmsSearch', 'searchEmails', 'searchDriveFiles'],
  'upload-to-dms': ['dmsUpload'],
};
